/**
 * Farbige Code-Blöcke für gängige Programmiersprachen.
 *
 * highlight.js wird nur mit dem Kern gebündelt; jede Sprache ist ein eigener,
 * erst bei Bedarf geladener Chunk. Ein Dokument ohne Codeblöcke lädt also gar
 * nichts nach. Gefärbt wird nach dem Rendern (wie Mermaid/TikZ), damit die
 * Vorschau beim Tippen sofort steht.
 */

import type { HLJSApi } from "highlight.js";

type Loader = () => Promise<{ default: unknown }>;

/** Sprachen, die in Schule/Studium tatsächlich vorkommen. */
const LANGUAGES: Record<string, Loader> = {
  bash: () => import("highlight.js/lib/languages/bash"),
  c: () => import("highlight.js/lib/languages/c"),
  cpp: () => import("highlight.js/lib/languages/cpp"),
  csharp: () => import("highlight.js/lib/languages/csharp"),
  css: () => import("highlight.js/lib/languages/css"),
  dart: () => import("highlight.js/lib/languages/dart"),
  diff: () => import("highlight.js/lib/languages/diff"),
  go: () => import("highlight.js/lib/languages/go"),
  ini: () => import("highlight.js/lib/languages/ini"),
  java: () => import("highlight.js/lib/languages/java"),
  javascript: () => import("highlight.js/lib/languages/javascript"),
  json: () => import("highlight.js/lib/languages/json"),
  kotlin: () => import("highlight.js/lib/languages/kotlin"),
  latex: () => import("highlight.js/lib/languages/latex"),
  lua: () => import("highlight.js/lib/languages/lua"),
  markdown: () => import("highlight.js/lib/languages/markdown"),
  matlab: () => import("highlight.js/lib/languages/matlab"),
  php: () => import("highlight.js/lib/languages/php"),
  python: () => import("highlight.js/lib/languages/python"),
  r: () => import("highlight.js/lib/languages/r"),
  ruby: () => import("highlight.js/lib/languages/ruby"),
  rust: () => import("highlight.js/lib/languages/rust"),
  scss: () => import("highlight.js/lib/languages/scss"),
  sql: () => import("highlight.js/lib/languages/sql"),
  swift: () => import("highlight.js/lib/languages/swift"),
  typescript: () => import("highlight.js/lib/languages/typescript"),
  xml: () => import("highlight.js/lib/languages/xml"),
  yaml: () => import("highlight.js/lib/languages/yaml"),
};

/** Kurzschreibweisen, wie man sie üblicherweise hinter ``` schreibt. */
const ALIASES: Record<string, string> = {
  js: "javascript",
  jsx: "javascript",
  mjs: "javascript",
  cjs: "javascript",
  node: "javascript",
  ts: "typescript",
  tsx: "typescript",
  py: "python",
  py3: "python",
  python3: "python",
  sh: "bash",
  shell: "bash",
  zsh: "bash",
  console: "bash",
  "c++": "cpp",
  cc: "cpp",
  h: "cpp",
  hpp: "cpp",
  cs: "csharp",
  "c#": "csharp",
  html: "xml",
  htm: "xml",
  svg: "xml",
  xhtml: "xml",
  yml: "yaml",
  md: "markdown",
  golang: "go",
  rb: "ruby",
  kt: "kotlin",
  postgres: "sql",
  postgresql: "sql",
  mysql: "sql",
  psql: "sql",
  plsql: "sql",
  tex: "latex",
  conf: "ini",
  toml: "ini",
  objc: "cpp",
  "objective-c": "cpp",
};

/** Nicht färben – diese Blöcke werden anderweitig verarbeitet. */
const SKIP = new Set(["mermaid", "tikz"]);

let corePromise: Promise<HLJSApi> | null = null;
const registered = new Set<string>();

function core(): Promise<HLJSApi> {
  if (!corePromise) {
    corePromise = import("highlight.js/lib/core").then((m) => {
      const hljs = m.default as HLJSApi;
      hljs.configure({ ignoreUnescapedHTML: true, throwUnescapedHTML: false });
      return hljs;
    });
  }
  return corePromise;
}

export function normalizeLang(raw: string): string | null {
  const key = raw.trim().toLowerCase();
  if (!key || SKIP.has(key)) return null;
  const name = ALIASES[key] ?? key;
  return name in LANGUAGES ? name : null;
}

async function ensureLanguage(name: string): Promise<HLJSApi | null> {
  const hljs = await core();
  if (registered.has(name)) return hljs;
  const load = LANGUAGES[name];
  if (!load) return null;
  const mod = await load();
  hljs.registerLanguage(name, mod.default as never);
  registered.add(name);
  return hljs;
}

/**
 * Färbt alle noch ungefärbten Codeblöcke unterhalb von `root` ein und
 * beschriftet sie mit der erkannten Sprache.
 */
export async function hydrateCode(root: HTMLElement): Promise<void> {
  const blocks = Array.from(
    root.querySelectorAll<HTMLElement>("pre > code[class*='language-']")
  ).filter((el) => !el.dataset.hl);

  // Sprach-Etikett steht bereits synchron im Markup (markdown/index.ts,
  // fence-Regel) – hier wird nur noch eingefärbt.
  const jobs = new Map<string, HTMLElement[]>();
  for (const el of blocks) {
    const raw = /language-([\w+#.-]+)/.exec(el.className)?.[1] ?? "";
    const lang = normalizeLang(raw);
    if (!lang) continue;
    const list = jobs.get(lang);
    if (list) list.push(el);
    else jobs.set(lang, [el]);
  }
  if (!jobs.size) return;

  await Promise.all(
    Array.from(jobs, async ([lang, els]) => {
      const hljs = await ensureLanguage(lang).catch(() => null);
      if (!hljs) return;
      for (const el of els) {
        try {
          const { value } = hljs.highlight(el.textContent ?? "", {
            language: lang,
            ignoreIllegals: true,
          });
          el.innerHTML = value;
          el.classList.add("hljs");
          el.dataset.hl = lang;
        } catch {
          /* Sprache streikt -> uneingefärbter Code bleibt lesbar */
        }
      }
    })
  );
}
